import { useState } from 'react';
import { CalendarCheckIcon, PencilIcon, PhoneIcon } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import {
  Button,
  EmptyState,
  ErrorNotice,
  FactList,
  Heading,
  PageHeading,
  Panel,
  Pill,
  SkeletonTable,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatMoney } from '@/lib/cash-flow';
import { eventStageTone } from '@/lib/event-status';
import { readOpportunityContext, type OpportunityContext } from '@/lib/sales-data';
import {
  FOLLOW_UP_BUCKET,
  salesReturnTo,
  formatDay,
  isNotAllowed,
  readOpportunityPage,
  type FollowUp,
  type RepWriteApplied,
} from '@/lib/sales-workspace';
import { CompleteFollowUpDialog } from '@/components/sales-workspace/CompleteFollowUpDialog';
import { DealLink } from '@/components/sales-workspace/DealLink';
import { AccountLink, ContactFlags, ContactLink, EngagementTimeline, type TimelineEntry } from '@/components/sales-workspace/sales-records';
import { DryRunNotice } from '@/components/sales-workspace/DryRunNotice';
import { EDIT_GROUP, EditOpportunityDialog, STAGE_CLOSED_LOST, type EditGroup } from '@/components/sales-workspace/EditOpportunityDialog';
import { LogCallDialog } from '@/components/sales-workspace/LogCallDialog';
import { RepConnectionNotice, useCanWriteAsRep } from '@/components/sales-workspace/rep-access';
import { REPEAT_CLIENTS_PATH } from '@/lib/repeat-clients';

const SYNC_TIME = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const money = (value: number | null): string => (value === null ? '—' : formatMoney(value));

function EditButton({ label, disabled, onClick }: { readonly label: string; readonly disabled: boolean; readonly onClick: () => void }): React.ReactElement {
  return (
    <Button variant="secondary" size="compact" disabled={disabled} onClick={onClick} aria-label={label}>
      <PencilIcon aria-hidden className="size-4" /> Edit
    </Button>
  );
}

/** One facts card: a title, an edit button, and its facts. */
function FactsCard({
  title,
  action,
  children,
}: {
  readonly title: string;
  readonly action: React.ReactNode;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <Panel className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <Heading variant="card" as="h2">
          {title}
        </Heading>
        {action}
      </div>
      {children}
    </Panel>
  );
}

/** The people on a deal, with their role and whether they may be emailed or called. */
function PeopleCard({ context }: { readonly context: OpportunityContext }): React.ReactElement {
  return (
    <Panel className="p-5">
      <Heading variant="card" as="h2" className="mb-3">
        People on this deal
      </Heading>
      {context.contacts.length === 0 ? (
        <EmptyState title="No contacts on this deal" message="Contact roles added to the opportunity in Salesforce show here." />
      ) : (
        <ul>
          {context.contacts.map(({ contact, role, isPrimary }) => (
            <li key={contact.id} className="flex items-start justify-between gap-3 border-t border-portal-line py-3 first:border-t-0 first:pt-0">
              <span className="min-w-0">
                <ContactLink id={contact.id} name={contact.name} className="block text-portal-small font-bold text-brand hover:underline" />
                <span className="block text-portal-micro text-portal-muted">
                  {[role || 'No role', contact.title, contact.email || contact.phone].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="flex shrink-0 flex-wrap justify-end gap-1">
                {isPrimary ? <Pill tone="brand">Primary</Pill> : null}
                <ContactFlags contact={contact} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** What the account booked before this deal. */
function PastBookingsCard({ context }: { readonly context: OpportunityContext }): React.ReactElement {
  return (
    <Panel className="p-5">
      <Heading variant="card" as="h2" className="mb-3">
        Past bookings
      </Heading>
      {context.pastBookings.length === 0 ? (
        <EmptyState title="No past bookings" message="Earlier events this account signed for show here, with program, date and value." />
      ) : (
        <ul>
          {context.pastBookings.map((booking) => (
            <li key={booking.id} className="flex items-start justify-between gap-3 border-t border-portal-line py-3 first:border-t-0 first:pt-0">
              <span className="min-w-0">
                <DealLink dealId={booking.id} className="block text-portal-small font-bold text-brand hover:underline">
                  {booking.programName || booking.name || 'Untitled deal'}
                </DealLink>
                <span className="block text-portal-micro text-portal-muted">
                  {formatDay(booking.eventDate ?? booking.signedDate, { withYear: true })}
                  {booking.participants ? ` · ${booking.participants} people` : ''}
                </span>
              </span>
              <span className="shrink-0 text-portal-small whitespace-nowrap">{money(booking.totalCost)}</span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** Card body while the deal's context loads. */
function ContextLoading({ title }: { readonly title: string }): React.ReactElement {
  return (
    <Panel className="p-5">
      <Heading variant="card" as="h2" className="mb-3">
        {title}
      </Heading>
      <SkeletonTable rows={3} columns={['w-[60%]', 'w-[30%]']} label={`Loading ${title.toLowerCase()}…`} />
    </Panel>
  );
}

/**
 * One deal in the sales workspace: its facts, follow-ups and recent activity
 * from the mirrors, with stage, details and pricing edited in small modals
 * and saved to Salesforce as the rep.
 *
 * Dark: the API answers 403 to anyone not on its allow-list.
 *
 * @returns The page.
 */
export function SalesOpportunityPage(): React.ReactElement {
  const { opportunityId = '' } = useParams();
  const routerLocation = useLocation();
  // Back to the view the deal was opened from (rep, tab, stage); else the owner's day.
  const cameFrom = salesReturnTo(routerLocation.state);
  const queryClient = useQueryClient();
  const canWrite = useCanWriteAsRep();
  const [editing, setEditing] = useState<EditGroup | null>(null);
  const [completing, setCompleting] = useState<FollowUp | null>(null);
  const [calling, setCalling] = useState(false);
  const [lastWrite, setLastWrite] = useState<RepWriteApplied | null>(null);

  const queryKey = ['sales-opportunity', opportunityId] as const;
  const contextKey = ['sales-opportunity-context', opportunityId] as const;
  const retry = (count: number, error: unknown): boolean => !isNotAllowed(error) && count < 2;
  const page = useQuery({ queryKey, queryFn: ({ signal }) => readOpportunityPage(opportunityId, signal), retry });
  // Who is on the deal, what the account booked before, and every touch with names.
  const context = useQuery({ queryKey: contextKey, queryFn: ({ signal }) => readOpportunityContext(opportunityId, signal), retry });

  if (page.isError && isNotAllowed(page.error)) return <Navigate to="/events" replace />;

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: contextKey });
  };
  const onDone = (result: RepWriteApplied): void => {
    setEditing(null);
    setCompleting(null);
    setCalling(false);
    setLastWrite(result);
    refresh();
  };

  if (page.isPending) {
    return (
      <section className="animate-portal-rise">
        <SkeletonTable rows={6} columns={['w-[30%]', 'w-[50%]']} label="Loading the opportunity…" />
      </section>
    );
  }
  if (page.isError) {
    return (
      <section className="animate-portal-rise">
        <PageHeading eyebrow="SALES" backTo={cameFrom ?? REPEAT_CLIENTS_PATH} title="Opportunity" />
        <ErrorNotice title="Opportunity unavailable" message={errorMessage(page.error)} />
      </section>
    );
  }

  const { deal, followUps, activity, today } = page.data;
  const editable = canWrite && !deal.isWon;
  const location = [deal.eventCity, deal.eventState].filter(Boolean).join(', ');
  const accountId = context.data?.deal.accountId ?? '';
  // Open to-dos already sit under Follow-ups. Without the context, fall back
  // to the page's own activity (no meetings, no names) rather than nothing.
  const openFollowUps = new Set(followUps.map((followUp) => followUp.taskId));
  const touches: readonly TimelineEntry[] = context.data ? context.data.timeline.filter((item) => !openFollowUps.has(item.id)) : activity;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        backTo={cameFrom ?? REPEAT_CLIENTS_PATH}
        title={deal.name || 'Untitled opportunity'}
        subtitle={
          <>
            {accountId ? <AccountLink id={accountId} name={deal.accountName} /> : deal.accountName}
            {deal.accountName && deal.ownerName ? ' · ' : ''}
            {deal.ownerName ? `Owner: ${deal.ownerName}` : ''}
          </>
        }
        action={
          <Button disabled={!canWrite} onClick={() => setCalling(true)}>
            <PhoneIcon aria-hidden className="size-4" /> Log a call
          </Button>
        }
      />
      <RepConnectionNotice />
      {lastWrite ? <DryRunNotice result={lastWrite} onDismiss={() => setLastWrite(null)} /> : null}
      {deal.isWon ? (
        <div className="mb-4">
          <ErrorNotice inline tone="warning" message="This deal is signed. Change it through its contract, or in Salesforce." />
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <FactsCard title="Stage" action={<EditButton label="Change stage" disabled={!editable} onClick={() => setEditing(EDIT_GROUP.stage)} />}>
          <div className="mb-3">
            <Pill tone={eventStageTone(deal.stageName)}>{deal.stageName}</Pill>
          </div>
          <FactList
            facts={[
              ...(deal.stageName === STAGE_CLOSED_LOST ? [{ label: 'Reason lost', value: deal.reasonLost || '—' }] : []),
              { label: 'Close date', value: formatDay(deal.closeDate, { withYear: true }) },
              { label: 'Next follow-up', value: formatDay(deal.nextFollowUp) },
            ]}
            columns={2}
          />
        </FactsCard>
        <FactsCard title="Deal details" action={<EditButton label="Edit deal details" disabled={!editable} onClick={() => setEditing(EDIT_GROUP.details)} />}>
          <FactList
            facts={[
              { label: 'Program', value: deal.programName || '—' },
              { label: 'Event date', value: formatDay(deal.eventDate, { withYear: true }) },
              { label: 'Participants', value: deal.participants ?? '—' },
              { label: 'Location', value: location || '—' },
            ]}
            columns={2}
          />
        </FactsCard>
        <FactsCard title="Pricing" action={<EditButton label="Edit pricing" disabled={!editable} onClick={() => setEditing(EDIT_GROUP.pricing)} />}>
          <FactList
            facts={[
              { label: 'Price list', value: deal.priceList || '—' },
              { label: 'Cost per person', value: money(deal.perPersonPrice) },
              { label: 'List price', value: money(deal.listPrice) },
              { label: 'Total program cost', value: money(deal.totalProgramCost) },
              { label: 'Total cost', value: money(deal.totalCost) },
            ]}
            columns={2}
          />
        </FactsCard>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel className="p-5">
          <Heading variant="card" as="h2" className="mb-3">
            Follow-ups
          </Heading>
          {followUps.length === 0 ? (
            <EmptyState
              icon={<CalendarCheckIcon aria-hidden className="size-6" />}
              title="No open follow-ups"
              message="Open to-dos on this deal show here. Completing one sets the next."
            />
          ) : (
            <ul>
              {followUps.map((followUp) => (
                <li key={followUp.taskId} className="flex items-center justify-between gap-3 border-t border-portal-line py-3 first:border-t-0 first:pt-0">
                  <span className="min-w-0">
                    <b className="block text-portal-small">{followUp.subject || '(no subject)'}</b>
                    <span className={`block text-portal-micro ${followUp.bucket === FOLLOW_UP_BUCKET.overdue ? 'font-bold text-rose-700' : 'text-portal-muted'}`}>
                      Due {formatDay(followUp.dueDate)}
                      {followUp.bucket === FOLLOW_UP_BUCKET.overdue ? ' (overdue)' : ''}
                    </span>
                  </span>
                  <Button size="compact" disabled={!canWrite} onClick={() => setCompleting(followUp)}>
                    Complete
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
        {context.isPending ? (
          <ContextLoading title="Recent activity" />
        ) : (
          <Panel className="p-5">
            <Heading variant="card" as="h2" className="mb-3">
              Recent activity
            </Heading>
            <EngagementTimeline items={touches} emptyMessage="Logged emails, calls, meetings, completed to-dos and stage changes show here." />
          </Panel>
        )}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {context.isError ? (
          <div className="lg:col-span-2">
            <ErrorNotice inline message={`People on this deal and past bookings could not load. ${errorMessage(context.error)}`} />
          </div>
        ) : context.isPending ? (
          <>
            <ContextLoading title="People on this deal" />
            <ContextLoading title="Past bookings" />
          </>
        ) : (
          <>
            <PeopleCard context={context.data} />
            <PastBookingsCard context={context.data} />
          </>
        )}
      </div>
      <p className="mt-4 text-portal-micro text-portal-muted">
        From the Salesforce mirror, last synced {SYNC_TIME.format(new Date(deal.mirroredAt))}. Edit forms read Salesforce live.
      </p>

      <EditOpportunityDialog opportunityId={deal.id} group={editing} onClose={() => setEditing(null)} onDone={onDone} />
      <CompleteFollowUpDialog
        followUp={completing}
        today={today}
        onClose={() => setCompleting(null)}
        onDone={onDone}
        onReload={() => {
          setCompleting(null);
          refresh();
        }}
      />
      <LogCallDialog deal={calling ? deal : null} onClose={() => setCalling(false)} onDone={onDone} />
    </section>
  );
}

